/*
 * Copyright (c) 2022 SARL SIGMA POLYNESIA
 */

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useFieldArray, useFormContext } from '@jalik/react-form'
import { useCallback, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { inverseAmounts } from '../../lib/common/amount.mjs'
import TransactionSelectionModal from '../transaction/TransactionSelectionModal'
import DeleteInvoiceTransactionModal from './DeleteInvoiceTransactionModal'
import InvoiceDetailsItem from './InvoiceDetailsItem'
import { ActionIcon, Group, NumberInput, Table } from '@mantine/core'
import { faPlusCircle } from '@fortawesome/free-solid-svg-icons/faPlusCircle'
import { faFolderOpen } from '@fortawesome/free-solid-svg-icons/faFolderOpen'
import NoData from '../../../../client/js/components/NoData'
import FormNumberField from '../../../../client/js/components/form/fields/FormNumberField'

export type InvoiceDetailsProps = {
  disabled?: boolean,
  locked?: boolean
}

function InvoiceDetails (props: InvoiceDetailsProps) {
  const { disabled, locked } = props
  const intl = useIntl()
  const form = useFormContext()
  const { values } = form
  const { append, items, remove } = useFieldArray<Partial<Transaction>, Invoice>({
    name: 'Transactions',
    context: form,
    defaultValue: {}
  })

  const transactions = useMemo(() => (items.map((el) => el.value)), [items])

  // Calculate total excluding tax.
  const totalExcludingTax = useMemo(() => (
    transactions
      .map((el) => el.amount)
      .filter((v) => !Number.isNaN(v))
      .reduce((v, r) => r + v, 0)
  ), [transactions])

  const isDisabled = useMemo(() => (
    disabled || (values.paid && locked) || !values.estateId
  ), [disabled, locked, values.estateId, values.paid])

  const transactionFilter = useMemo(() => ({
    customerId: values.customerId,
    estateId: values.estateId,
    leaseId: values.leaseId,
    // Return expenses only.
    expense: true,
    // Ignore current transactions to avoid duplicates.
    exclude: transactions.map((el) => el.id).filter((el) => el != null),
    // Ignore paid transactions or already attached to an invoice.
    paid: false,
    withInvoice: false
  }), [transactions, values.customerId, values.estateId, values.leaseId])

  // Handlers

  const handleClickAdd = useCallback(() => {
    append({
      paid: false,
      category: null,
      expense: true,
      label: null,
      amount: null,
      vat: null,
      cps: null
    })
  }, [append])

  const [isSelectModalOpen, setIsSelectModalOpen] = useState(false)

  const handleClickSelect = useCallback(() => {
    setIsSelectModalOpen(true)
  }, [])

  const handleSelectTransaction = useCallback((transaction: Partial<Transaction>) => {
    append(inverseAmounts(transaction))
    setIsSelectModalOpen(false)
  }, [append])

  const toggleSelectModal = useCallback(() => {
    setIsSelectModalOpen((s) => !s)
  }, [])

  const [indexToDelete, setIndexToDelete] = useState<number>(null)

  const handleRemove = useCallback((index: number) => () => {
    setIndexToDelete(index)
  }, [])

  const handleDelete = useCallback(() => {
    remove(indexToDelete)
    setIndexToDelete(null)
  }, [indexToDelete, remove])

  return (
    <fieldset>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Td>
              <Group gap="xs" wrap="nowrap">
                <ActionIcon
                  disabled={isDisabled}
                  onClick={handleClickAdd}
                  title={intl.formatMessage({
                    id: 'add_line',
                    defaultMessage: 'Ajouter une ligne'
                  })}
                >
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faPlusCircle}
                  />
                </ActionIcon>
                <ActionIcon
                  disabled={isDisabled}
                  onClick={handleClickSelect}
                  title={intl.formatMessage({
                    id: 'select_transaction',
                    defaultMessage: 'Sélectionner une transaction'
                  })}
                >
                  <FontAwesomeIcon
                    fixedWidth
                    icon={faFolderOpen}
                  />
                </ActionIcon>
              </Group>
            </Table.Td>
            <Table.Th>
              <FormattedMessage
                id="paid"
                defaultMessage="Payé"
              />
            </Table.Th>
            <Table.Th w="30%">
              <FormattedMessage
                id="label"
                defaultMessage="Libellé"
              />
            </Table.Th>
            <Table.Th>
              <FormattedMessage
                id="category"
                defaultMessage="Catégorie"
              />
            </Table.Th>
            <Table.Th>
              <FormattedMessage
                id="amount_excluding_tax"
                defaultMessage="Montant HT"
              />
            </Table.Th>
            <Table.Th>
              <FormattedMessage
                id="vat"
                defaultMessage="TVA"
              />
            </Table.Th>
            <Table.Th>
              <FormattedMessage
                id="cps"
                defaultMessage="CPS"
              />
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((field, index) => (
            <InvoiceDetailsItem
              key={field.key}
              index={index}
              locked={locked}
              name={field.name}
              onRemove={handleRemove(index)}
            />
          ))}
          {items.length === 0
            ? <Table.Tr>
              <Table.Td colSpan={7}>
                <NoData />
              </Table.Td>
            </Table.Tr>
            : null}
        </Table.Tbody>
        <Table.Tfoot>
          <Table.Tr>
            <Table.Td />
            <Table.Td />
            <Table.Td />
            <Table.Td />
            <Table.Td>
              <NumberInput
                disabled
                id="total_exc_tax"
                thousandSeparator=" "
                label={intl.formatMessage({
                  id: 'total_excluding_tax',
                  defaultMessage: 'Total HT'
                })}
                value={totalExcludingTax}
              />
            </Table.Td>
            <Table.Td />
            <Table.Td />
          </Table.Tr>
          <Table.Tr>
            <Table.Td />
            <Table.Td />
            <Table.Td />
            <Table.Td />
            <Table.Td>
              <FormNumberField
                disabled
                label={intl.formatMessage({
                  id: 'total_tax_included',
                  defaultMessage: 'Total TTC'
                })}
                name="total"
              />
            </Table.Td>
            <Table.Td>
              <FormNumberField
                disabled
                label={intl.formatMessage({
                  id: 'including_vat',
                  defaultMessage: 'Dont TVA'
                })}
                name="vat"
              />
            </Table.Td>
            <Table.Td>
              <FormNumberField
                disabled
                label={intl.formatMessage({
                  id: 'including_cps',
                  defaultMessage: 'Dont CPS'
                })}
                name="cps"
              />
            </Table.Td>
          </Table.Tr>
        </Table.Tfoot>
      </Table>

      <DeleteInvoiceTransactionModal
        opened={indexToDelete != null}
        onConfirm={handleDelete}
        onClose={() => setIndexToDelete(null)}
        transactionId={transactions[indexToDelete]?.id}
      />

      <TransactionSelectionModal
        queryFilter={transactionFilter}
        onClickResult={handleSelectTransaction}
        onClose={toggleSelectModal}
        opened={isSelectModalOpen}
      />
    </fieldset>
  )
}

export default InvoiceDetails
